// SlotConfirmStep — the "review and confirm" view for Schedule and Reschedule. A slot summary
// card, an optional issue panel attached beneath it, and a "What happens next" checklist of the
// system actions that run on confirm. Pure presentational; the caller owns the save.
//
// ── THE FIVE STATES (QA-93) ──────────────────────────────────────────────────────────────────
// The card is the last thing a therapist reads before an email goes to a couple, so it has to say
// what it is about to do AND what is already in that time. Five shapes, one component:
//
//   1. no conflict        summary alone
//   2. double booked      issue panel, `conflict` tone — another couple has this time
//   3. busy               issue panel, `conflict` tone — the therapist's own calendar hold
//   4. outside hours      issue panel, `note` tone — their own working day, not a clash
//   5. reschedule         TWO summaries side by side, current → new, with the arrow between
//
// ── WHY THESE TONES ──────────────────────────────────────────────────────────────────────────
// `conflict` / `note` are NOT invented here: they are `slotTooltipCopy`'s own two tones, and the
// hover card over the grid cell has already used them to describe this exact hour. A card that
// painted "outside working hours" the same as "double-booked" would contradict the tooltip the
// therapist read one click earlier.
//
// The split is by SEVERITY, not by kind: `conflict` answers "something else is in this time",
// `note` answers "this time is fine, it is just outside the day you set". Double-booked and Busy
// share a tone because they share that answer, and are told apart by their icon and their words —
// which is also how `REASON_COPY` tells them apart ("Double-booked" vs "Admin block").
//
// `danger` is available and deliberately unused by the mapping above: nothing on this screen is
// destructive. Reschedule cancels an invite, and that is stated in `What happens next` rather than
// coloured red.

import { Card } from "../Card";
import { IconTile } from "../IconTile";
import { AlertTriangleIcon, CalendarIcon } from "../icons";

export interface SlotSummary {
    /** "New" / "Current" — the eyebrow over the time. */
    eyebrow: string;
    /** "Wednesdays · 9:00 AM (BST)" — the headline. */
    primary: string;
    /** "50 min · Video call · 4 sessions · Aug 19 – Sep 9" — the meta line under it. */
    secondary?: string;
}

/** Which semantic role an issue carries. See the tone note above. */
export type SlotIssueTone = "conflict" | "note" | "danger";

/** One dated beat of an issue — "Wed Aug 26 · 9:00 AM — Adeyemi & Cole". */
export interface SlotIssueOccurrence {
    /** Pre-formatted by the caller: dates belong to whoever owns the timezone. */
    when: string;
    /**
     * Who or what is in the way. SERVER-SUPPLIED wherever it names another couple — that
     * disclosure is the server's call, not this component's (see `SeriesOccurrence.conflictDetail`
     * and QA-87). Omitted for an issue with nobody to name, like outside-hours.
     */
    detail?: string;
}

export interface SlotIssue {
    tone: SlotIssueTone;
    /** "2 sessions are double-booked" — states the fact, and its size when there is one. */
    headline: string;
    /** The dated beats. Empty or omitted for a slot-level issue with no per-date detail. */
    occurrences?: readonly SlotIssueOccurrence[];
}

export interface SlotConfirmStepProps {
    /** Omit when nothing is being replaced (scheduling a first slot). Present ⇒ reschedule. */
    from?: SlotSummary;
    to: SlotSummary;
    /** What is already in this time, if anything. Omit for the no-conflict state. */
    issue?: SlotIssue;
    /** Checklist of actions that will happen when the user confirms. */
    actions: string[];
}

const ISSUE_TONE: Record<SlotIssueTone, { panel: string; ink: string; bullet: string }> = {
    // #faf1e5 / #9a5e24 — the same pair the grid's conflict cells and the series CONFLICT badge
    // carry, so one hour reads the same on all three surfaces.
    conflict: { panel: "bg-warning-bg", ink: "text-warning", bullet: "bg-warning" },
    // #ecf0f4 / #4d6a83 — `--info`, which this wizard already uses for "a note you may proceed
    // past": the outside-hours hover on the grid, and the appended-week pill on the series screen.
    note: { panel: "bg-info-bg", ink: "text-info", bullet: "bg-info" },
    danger: { panel: "bg-danger-bg", ink: "text-danger", bullet: "bg-danger" },
};

/**
 * The summary's leading tile — the SAME `IconTile` the grid's proposal footer uses (QA-94).
 *
 * It was a 38px circle with a `--cell-partly-bd` ring, which made the two screens either side of
 * one click show the same calendar two different ways — and borrowed a token that belongs to a grid
 * CELL for a card that has no cells in it. One tile, both surfaces, DS tokens only.
 *
 * Not toned: the tile is a container for an icon, not a status.
 *
 * ONE PER CARD (QA-97). The reschedule diff used to lead both halves with it, which made the arrow
 * between them the third calendar in a row about a single session.
 *
 * And it belongs to the ROW, not to either half (QA-98): it labels the card as being about a
 * session, which is true of both times. Nested inside `current` it read as that half's own icon and
 * pushed the arrow off-centre.
 */
function SummaryGlyph() {
    return (
        <IconTile>
            <CalendarIcon size={20} />
        </IconTile>
    );
}

/**
 * The eyebrow carries the whole distinction now that the fills are gone (QA-97).
 *
 *   new      --success    what the therapist is about to have
 *   current  --warning    what they are about to lose
 *   neutral  --secondary  a session that simply IS
 *
 * `--warning` and not `--neutral` for `current`: on a move, the outgoing time is not merely older,
 * it is the one whose invite gets cancelled. That is the same role the grid gives a conflict, and
 * the reason the word "Current" should catch the eye at all.
 *
 * `neutral` exists for the surfaces that are not choosing anything. `new` and `current` are the
 * two halves of a DECISION, and their colour is what makes the difference between them legible;
 * on a screen that states one booked session and offers no alternative to it, that green is
 * answering a question nobody asked. Couple onboarding's Homebase is the first such caller.
 */
export type SlotEyebrowTone = "current" | "new" | "neutral";

const EYEBROW_TONE: Record<SlotEyebrowTone, string> = {
    // `!` because `.dova-eyebrow` sets its own `color` and tokens.css is unlayered and imported
    // after Tailwind — a bare `text-*` here is inert, the same trap the couples-list subtitle
    // documents, and the reason `!p-0` is the DS's established idiom.
    new: "!text-success",
    current: "!text-warning",
    neutral: "!text-secondary",
};

function SummaryBody({ summary, tone = "new" }: { summary: SlotSummary; tone?: SlotEyebrowTone }) {
    return (
        <div className="flex min-w-0 flex-col gap-1">
            <span className={`dova-eyebrow ${EYEBROW_TONE[tone]}`}>{summary.eyebrow}</span>
            <div className="font-sans text-base font-semibold leading-[1.35] text-primary">{summary.primary}</div>
            {summary.secondary && (
                <div className="font-sans text-xs leading-[1.45] text-secondary">{summary.secondary}</div>
            )}
        </div>
    );
}

/**
 * One summary panel — no fill, no border, and it HUGS its content (QA-98).
 *
 * The fills went in QA-97: two tinted boxes inside a bordered card was three enclosures deep for
 * "this time is becoming that time". The eyebrows carry that distinction now.
 *
 * `flex-1` went with QA-98. Two halves each claiming half the row left the arrow stranded in a gap
 * of its own making — far from the time it points away from and far from the one it points at. The
 * row now reads as a phrase: icon, the old time, the arrow, the new one, in that order and no
 * wider than they need. `min-w-0` stays so a long label truncates rather than forcing the row wide.
 *
 * `--space-4` HORIZONTAL padding (QA-99), so each time has room of its own rather than sitting hard
 * against the arrow and the icon. Horizontal only: the row already sets the vertical rhythm, and
 * padding the block would push the two halves out of line with the icon beside them.
 */
function SummaryPanel({ summary, tone }: { summary: SlotSummary; tone: "current" | "new" }) {
    return (
        <div
            data-testid={`slot-summary-${tone}`}
            className="flex min-w-0 items-center px-[var(--space-16)]"
        >
            <SummaryBody summary={summary} tone={tone} />
        </div>
    );
}

function IssuePanel({ issue }: { issue: SlotIssue }) {
    const tone = ISSUE_TONE[issue.tone];
    return (
        <div
            data-testid="slot-issue"
            data-tone={issue.tone}
            className={`flex items-start gap-4 border-t border-border px-4 py-4 ${tone.panel}`}
        >
            <span aria-hidden className={`mt-px shrink-0 ${tone.ink}`}>
                <AlertTriangleIcon size={18} />
            </span>
            <div className="flex min-w-0 flex-col gap-2">
                <span
                    data-testid="slot-issue-headline"
                    className={`font-sans text-sm font-semibold leading-[1.4] ${tone.ink}`}
                >
                    {issue.headline}
                </span>
                {issue.occurrences && issue.occurrences.length > 0 && (
                    <ul className="flex flex-col gap-1">
                        {issue.occurrences.map((occ) => (
                            <li
                                key={`${occ.when}-${occ.detail ?? ""}`}
                                data-testid="slot-issue-occurrence"
                                className="flex items-baseline gap-2 font-sans text-xs leading-[1.5]"
                            >
                                <span aria-hidden className={`mt-2 h-1 w-1 shrink-0 rounded-full ${tone.bullet}`} />
                                <span className="min-w-0">
                                    <span className="font-medium text-primary">{occ.when}</span>
                                    {/* The em dash belongs to the SEPARATOR, not to either side, so
                                        an occurrence with nobody to name renders no dangling rule. */}
                                    {occ.detail && <span className="text-secondary"> — {occ.detail}</span>}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export function SlotConfirmStep({ from, to, issue, actions }: SlotConfirmStepProps) {
    return (
        <div className="flex flex-col gap-6">
            {/* ONE card whether or not there is an issue, and whether or not this is a move. The
                issue is a REGION of the card it describes, not a separate object beside it — the
                clash belongs to that time, and a detached panel would leave the therapist matching
                the two up. `overflow-hidden` is what lets the tinted region reach the rounded
                corners. Both branches keep that shape; the single-summary one now lives in
                `SlotSummaryCard` so a screen that is not confirming anything can reuse it. */}
            {from ? (
                <Card tone="soft" className="overflow-hidden" data-testid="slot-confirm-card">
                    {/* icon | current | arrow | new (QA-98). The glyph is the ROW's first element,
                        not the outgoing half's, so the arrow sits between the two times rather than
                        being pushed off-centre by an icon on one side only.

                        `--space-4` (16px) all round, a little more than the `px-4 py-4` this and
                        the single-summary branch shared — and uniform, which `py-4` was not. On
                        the DS scale rather than Tailwind's numerics, whose 15px root made "4" and
                        "3.5" resolve to 15 and 13.125. */}
                    <div className="flex items-center gap-4 p-[var(--space-16)]">
                        <SummaryGlyph />
                        <SummaryPanel summary={from} tone="current" />
                        <span
                            aria-hidden
                            data-testid="slot-diff-arrow"
                            className="flex shrink-0 items-center self-center text-lg leading-none text-tertiary"
                        >
                            →
                        </span>
                        <SummaryPanel summary={to} tone="new" />
                    </div>
                    {issue && <IssuePanel issue={issue} />}
                </Card>
            ) : (
                <SlotSummaryCard summary={to} issue={issue} />
            )}

            <WhatHappensNext actions={actions} />
        </div>
    );
}

/**
 * The single-summary card, on its own: the calendar tile, the time, and the issue
 * region beneath it when there is one.
 *
 * Extracted from `SlotConfirmStep` (no markup change) for the same reason
 * `WhatHappensNext` was: a surface that shows a booked session but is NOT confirming
 * anything needs this card without the rest of the step. Couple onboarding's Homebase
 * is the first such caller — it states a session that already exists, so
 * "What happens next" would be promising an act nobody is about to take, and
 * `SlotConfirmStep` renders that block unconditionally.
 *
 * NOT the reschedule row. That branch is a different layout (two panels with an
 * arrow between, glyph belonging to the row rather than either half, QA-98) and it
 * stays inside `SlotConfirmStep`, where its comments live.
 */
export function SlotSummaryCard({
    summary,
    issue,
    eyebrowTone = "new",
}: {
    summary: SlotSummary;
    issue?: SlotIssue;
    /** Defaults to `new`, so `SlotConfirmStep` keeps the colour it decides with. A caller that
     *  is only STATING a session should pass `neutral`; see the tone note above. */
    eyebrowTone?: SlotEyebrowTone;
}) {
    return (
        <Card tone="soft" className="overflow-hidden" data-testid="slot-confirm-card">
            {/* `items-center` (QA-94): the tile and the text block are two objects on one
                row, so they centre against each other rather than the tile hanging from the
                eyebrow. The diff row's panels do the same, one level in. */}
            <div className="flex items-center gap-4 p-[var(--space-16)]">
                <SummaryGlyph />
                <SummaryBody summary={summary} tone={eyebrowTone} />
            </div>
            {issue && <IssuePanel issue={issue} />}
        </Card>
    );
}

/**
 * The "what happens next" checklist, on its own.
 *
 * Extracted from `SlotConfirmStep` (no markup change) so a confirmation that is NOT a
 * before/after diff can carry the same promise. The recurring booking flow ends on a list of
 * dated occurrences rather than a pair of cards, but it commits the same outward act — an email
 * to the couple — and that has to be stated in the same words, in the same place, on every flow.
 */
export function WhatHappensNext({ actions }: { actions: string[] }) {
    return (
        <div>
            <div className="dova-eyebrow mb-2">What happens next</div>
            {/* text-base, not text-sm. Raised with the create-couple confirmation
                rather than for it: this block is the shared promise, and two
                confirmations that state the same kind of act in two sizes are
                two components pretending to be one. */}
            <ul className="flex flex-col gap-2 font-sans text-base leading-[1.5] text-primary">
                {actions.map((action) => (
                    <li key={action} className="flex items-start gap-2">
                        <span aria-hidden className="mt-px shrink-0 font-semibold text-success">
                            ✓
                        </span>
                        <span>{action}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
